// Components
import RenderBankCode from '@/app/modules/contracts/components/bankCode/RenderBankCode';
import RenderDateWithFromTime from '@/common-utils/components/renders/date/RenderDateWithFromTime';
import RenderApplication from '@/app/modules/contracts/components/application/RenderApplication';
import RenderInternationalizedAmount from '@/common-utils/components/renders/currency/RenderInternationalizedAmount';
import Entity from '@/common-utils/components/renders/entity/Entity';
import DropdownRemoteCollectionsActionsRow from '../../dropdown/DropdownRemoteCollectionsActionsRow';
import DetailsButton from '@/common-utils/components/renders/detailsButton/DetailsButton';
import { Badge } from '@/common-utils/components/ui/badge';
// Common
import generateColumnDefByField from '@/common-utils/functions/generateColumnDefByField';
import getObjectAttributeFromStringKey from '@/common-utils/functions/getObjectAttributeFromStringKey';
import generateNestedFieldAccessor from '@/common-utils/functions/generateNestedFieldAccessor';
import remoteCollectionFields from '../../../fields/remoteCollectionFields';
import RouteUrls from '@/app/root/enums/RouteUrls';
import Permissions from '@/app/permissions/Permissions';
import REMOTE_COLLECTION_RECONCILIATION_CODE from '../../../enums/RemoteCollectionReconciliationCode';
import POS_TERMINALS_TABS from '@/app/root/enums/tabs/PosTerminalsTabs';
import SUBSCRIPTION_AND_TERMINAL_DETAILS_TABS from '@/app/root/enums/tabs/SubscriptionAndTerminalDetailsTabs';
// Lang
import { nsRemoteCollection } from '@/app/i18n/namespaces';

export const columnsRemoteCollectionsId = 'remoteCollection';

export const columnsRemoteCollections = (headerPackage) => {

    return ([
        generateColumnDefByField({
            field: remoteCollectionFields.DETAILS,
            headerPackage: headerPackage,
            customCellFn: ({ row }) => (
                <DetailsButton
                    pathParams={
                        {
                            subscriptionId: row.original.subscription,
                            posTerminalId: row.original.posTerminal.uuid,
                            remoteCollectionId: row.original.uuid
                        }
                    }
                    baseUrl={RouteUrls.REMOTE_COLLECTION_TRANSACTIONS.url}
                    urlParams={{
                        view: POS_TERMINALS_TABS.SUBSCRIPTIONS.name,
                        viewDetail: SUBSCRIPTION_AND_TERMINAL_DETAILS_TABS.REMOTE_COLLECTIONS.name,
                        posTerminalId: row.original.posTerminal.uuid
                    }}
                    requiredPermissions={[
                        Permissions.PAYMENT_LIST_REMOTE_COLLECTION_TRANSACTIONS.name,
                        Permissions.PAYMENT_LIST_REMOTE_COLLECTION_PLBS.name
                    ]}
                />
            ),
            show: !headerPackage.selectionMode
        }),
        generateColumnDefByField({
            field: remoteCollectionFields.UUID,
            headerPackage: headerPackage,
            customCellFn: ({ cell }) => {
                return cell && cell.row.original &&
                    <DropdownRemoteCollectionsActionsRow
                        remoteCollection={cell.row.original}
                        setLaunchRefetch={headerPackage.setLaunchRefetch}
                    />;
            }
        }),
        generateColumnDefByField({
            field: remoteCollectionFields.LOCAL_TRANSACTION_DATE_TIME,
            headerPackage: headerPackage,
            customCellFn: ({ cell }) => (
                cell &&
                <RenderDateWithFromTime value={cell.getValue(remoteCollectionFields.LOCAL_TRANSACTION_DATE_TIME.key)} />
            )
        }),
        generateColumnDefByField({
            field: remoteCollectionFields.APPLICATION,
            headerPackage: headerPackage,
            customCellFn: ({ cell }) => (
                cell &&
                <RenderApplication applicationCode={cell.getValue(remoteCollectionFields.APPLICATION.key)} />
            )
        }),
        generateColumnDefByField({
            field: remoteCollectionFields.CB_VERSION,
            headerPackage: headerPackage,
            customCellFn: ({ cell }) => (
                cell && cell.getValue(remoteCollectionFields.CB_VERSION.key) &&
                <span>{cell.getValue(remoteCollectionFields.CB_VERSION.key).replace(/^CB/, '')}</span>
            )
        }),
        generateColumnDefByField({
            field: remoteCollectionFields.CONTRACT,
            headerPackage: headerPackage
        }),
        generateColumnDefByField({
            field: remoteCollectionFields.POS_TERMINAL.LABEL,
            headerPackage: headerPackage,
            isLongText: true
        }),
        generateColumnDefByField({
            field: remoteCollectionFields.POS_TERMINAL.CUSTOM_LABEL,
            headerPackage: headerPackage,
            isLongText: true
        }),
        generateColumnDefByField({
            field: remoteCollectionFields.RANK,
            headerPackage: headerPackage
        }),
        generateColumnDefByField({
            field: remoteCollectionFields.POS_TERMINAL.SERIAL_NUMBER,
            headerPackage: headerPackage
        }),
        generateColumnDefByField({
            field: remoteCollectionFields.POS_TERMINAL.MANUFACTURER,
            headerPackage: headerPackage
        }),
        generateColumnDefByField({
            field: remoteCollectionFields.RECONCILIATION_NUMBER,
            headerPackage: headerPackage
        }),
        generateColumnDefByField({
            field: remoteCollectionFields.POS_REMITTANCE_NUMBER,
            headerPackage: headerPackage
        }),
        generateColumnDefByField({
            field: remoteCollectionFields.TOTAL_AMOUNT,
            headerPackage: headerPackage,
            customCellFn: ({ cell }) => {
                const currencyCode = getObjectAttributeFromStringKey(
                    cell.row.original,
                    generateNestedFieldAccessor(remoteCollectionFields.CURRENCY)
                );
                const totalAmount = cell.getValue(remoteCollectionFields.TOTAL_AMOUNT.key);
                const isRemoteCollectionEmpty = totalAmount === 0;
                return (
                    totalAmount !== null ?
                        <RenderInternationalizedAmount amount={totalAmount} currency={currencyCode} isRemoteCollectionEmpty={isRemoteCollectionEmpty} row={cell.row} />
                        : null
                );
            }
        }),
        generateColumnDefByField({
            field: remoteCollectionFields.RECONCILIATION_CODE,
            headerPackage: headerPackage,
            customCellFn: ({ cell }) => {
                const status = REMOTE_COLLECTION_RECONCILIATION_CODE[cell.getValue(remoteCollectionFields.RECONCILIATION_CODE.key)];
                return (
                    cell &&
                    <Badge entity={status.entity} namespace={status.namespace} variant={status.variant} />
                );
            }
        }),
        generateColumnDefByField({
            field: remoteCollectionFields.TRANSACTION_COUNT,
            headerPackage: headerPackage,
            customCellFn: ({ cell }) => {
                const count = (cell && cell.getValue(remoteCollectionFields.TRANSACTION_COUNT.key)) ?? null;
                return (
                    count !== null &&
                    <div>
                        <Entity
                            nowrap
                            entity={
                                count > 1 ?
                                    'count.transactions'
                                    : 'count.transaction'}
                            namespace={nsRemoteCollection}
                            data={{ transactionsCount: count }} />
                    </div>
                );
            }
        }),
        generateColumnDefByField({
            field: remoteCollectionFields.BANK_CODE,
            headerPackage: headerPackage,
            customCellFn: ({ cell }) => (
                cell &&
                <RenderBankCode bankCode={cell.getValue(remoteCollectionFields.BANK_CODE.key)} />
            )
        }),
        generateColumnDefByField({
            field: remoteCollectionFields.LEGAL_NAME,
            headerPackage: headerPackage
        }),
        generateColumnDefByField({
            field: remoteCollectionFields.REGISTRATION_NUMBER,
            headerPackage: headerPackage
        })
    ]);
};